import { collection, getDocs, getCountFromServer, query, where, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

export interface DashboardStats {
  inventoryValue: number;
  boothCount: number;
  skuCount: number;
  vendorCount: number;
  totalWallets: number;
  totalBalance: number;
  totalTransactions: number;
  totalVolume: number;
}

export interface HourlyTransactionData {
  hour: string;
  count: number;
  volume: number;
}

export interface SKUSalesData {
  skuId: string;
  skuName: string;
  shortName: string;
  boothId: string;
  boothName: string;
  salesCount: number;
}

export interface BoothOption {
  id: string;
  name: string;
}

export interface SKUSalesResult {
  salesData: SKUSalesData[];
  booths: BoothOption[];
}

/**
 * Get all dashboard statistics in a single batch
 * Optimized: Uses getCountFromServer for simple counts, only fetches full docs when needed for calculations
 */
export async function getDashboardStats(): Promise<DashboardStats> {
  // Fetch data in parallel - use count queries where we only need counts
  const [
    skusSnapshot,
    boothCountResult,
    vendorCountResult,
    walletsSnapshot,
    transactionCountResult,
    transactionsVolumeSnapshot,
  ] = await Promise.all([
    // Need full docs to calculate inventory value (price * quantity)
    getDocs(collection(db, 'skus')),
    // Only need count
    getCountFromServer(collection(db, 'booths')),
    // Only need count
    getCountFromServer(query(collection(db, 'users'), where('role', '==', 'vendor'))),
    // Need full docs to calculate balance and exclude booth wallets
    getDocs(collection(db, 'wallets')),
    // Only need count
    getCountFromServer(collection(db, 'transactions')),
    // Need full docs to calculate volume (could be optimized with aggregation queries in future)
    getDocs(collection(db, 'transactions')),
  ]);

  // Calculate inventory value (sum of price * quantity for all SKUs)
  let inventoryValue = 0;
  skusSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    inventoryValue += (data.price || 0) * (data.quantity || 0);
  });

  // Calculate wallet stats (excluding booth wallets)
  let totalBalance = 0;
  let totalWallets = 0;
  walletsSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    if (data.type !== 'booth' && data.ownerId !== 'booth') {
      totalBalance += data.balance || 0;
      totalWallets++;
    }
  });

  // Calculate transaction volume
  let totalVolume = 0;
  transactionsVolumeSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    totalVolume += data.amount || 0;
  });

  return {
    inventoryValue,
    boothCount: boothCountResult.data().count,
    skuCount: skusSnapshot.docs.length,
    vendorCount: vendorCountResult.data().count,
    totalWallets,
    totalBalance,
    totalTransactions: transactionCountResult.data().count,
    totalVolume,
  };
}

/**
 * Get transactions grouped by hour for the current day
 * Optimized: Uses timestamp filter to only fetch today's transactions instead of all transactions
 */
export async function getTodayTransactionsByHour(): Promise<HourlyTransactionData[]> {
  // Get start and end of today as Firestore Timestamps
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);

  // Query only today's transactions using Firestore timestamp filter
  const todayQuery = query(
    collection(db, 'transactions'),
    where('timestamp', '>=', Timestamp.fromDate(startOfToday)),
    where('timestamp', '<', Timestamp.fromDate(endOfToday))
  );
  const transactionsSnapshot = await getDocs(todayQuery);

  // Initialize all hours with zero
  const hourlyData: Map<number, { count: number; volume: number }> = new Map();
  for (let i = 0; i < 24; i++) {
    hourlyData.set(i, { count: 0, volume: 0 });
  }

  // Group transactions by hour
  transactionsSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    const timestamp = data.timestamp?.toDate();

    if (timestamp) {
      const hour = timestamp.getHours();
      const current = hourlyData.get(hour)!;
      current.count++;
      current.volume += data.amount || 0;
    }
  });

  // Convert to array format
  return Array.from(hourlyData.entries()).map(([hour, data]) => ({
    hour: `${hour.toString().padStart(2, '0')}:00`,
    count: data.count,
    volume: data.volume,
  }));
}

/**
 * Get SKU sales data for analytics, optionally filtered by booth
 * Also returns booth list to avoid redundant fetching in dashboard
 */
export async function getSKUSalesData(boothId?: string): Promise<SKUSalesResult> {
  // Fetch sales counters, SKUs, and booths in parallel
  const [salesSnapshot, skusSnapshot, boothsSnapshot] = await Promise.all([
    getDocs(collection(db, 'sku_sales_counters')),
    getDocs(collection(db, 'skus')),
    getDocs(collection(db, 'booths')),
  ]);

  // Build booth name lookup map and booth options list
  const boothNames = new Map<string, string>();
  const booths: BoothOption[] = [];
  boothsSnapshot.docs.forEach((doc) => {
    const name = doc.data().name;
    boothNames.set(doc.id, name);
    booths.push({ id: doc.id, name });
  });

  // Build SKU info lookup map
  const skuInfo = new Map<string, { name: string; shortName: string; boothId: string }>();
  skusSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    skuInfo.set(doc.id, {
      name: data.name,
      shortName: data.shortName,
      boothId: data.boothId,
    });
  });

  // Build sales data array
  const salesData: SKUSalesData[] = [];

  salesSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    const skuId = data.skuId || doc.id;
    const sku = skuInfo.get(skuId);

    if (!sku) {
      return; // Skip if SKU no longer exists
    }

    // Filter by booth if specified
    if (boothId && sku.boothId !== boothId) {
      return;
    }

    salesData.push({
      skuId,
      skuName: sku.name,
      shortName: sku.shortName,
      boothId: sku.boothId,
      boothName: boothNames.get(sku.boothId) || 'Estande Desconhecido',
      salesCount: data.salesCount || 0,
    });
  });

  // Sort by sales count descending
  return {
    salesData: salesData.sort((a, b) => b.salesCount - a.salesCount),
    booths,
  };
}
